/**
 * Pure helpers used by EditableField. Extracted from EditableField.tsx so
 * unit tests don't pull React / lucide-react / TanStack Query into Node's
 * test runner.
 */

export type FieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'date'
  | 'number'
  | 'currency'
  | 'boolean'
  | 'select';

/**
 * Default serialiser by field type. Strings come from the input as-is;
 * numbers need parseInt/parseFloat; date stays as ISO; currency converts
 * dollars → cents.
 */
export function defaultSerialise(type: FieldType) {
  return (raw: string): string | number | boolean | null => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    if (type === 'number')   return Math.round(Number(trimmed));
    if (type === 'currency') return Math.round(Number(trimmed) * 100);
    if (type === 'boolean')  return trimmed === 'true';
    return trimmed;
  };
}

/** Yes/No display + edit-string helpers for the boolean field type. */
export function boolDisplay(v: string | number | boolean | null | undefined): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '';
}

export function boolEditString(v: string | number | boolean | null | undefined): string {
  if (v === true) return 'true';
  if (v === false) return 'false';
  return '';
}

export function fmtCurrencyDollars(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return '';
  const dollars = typeof v === 'string' ? Number(v) : v;
  if (Number.isNaN(dollars)) return '';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(dollars);
}

export function fmtCentsAsDollars(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return '';
  const cents = typeof v === 'string' ? Number(v) : v;
  if (Number.isNaN(cents)) return '';
  return fmtCurrencyDollars(cents / 100);
}

export function editCentsAsDollars(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return '';
  const cents = typeof v === 'string' ? Number(v) : v;
  if (Number.isNaN(cents)) return '';
  return String(cents / 100);
}
